import type { EncounterSpecialty } from "@/api/encounters";
import {
  computeFullCodeDiff,
  type CodeItem,
  type CodeItemWithDiff,
} from "@/ai-coding-outputs/admin-coding/codeComparison";
import HrgPill from "@/ai-coding-outputs/admin-coding/HrgPill";
import { GoldenDatasetMenuItems } from "@/ai-coding-outputs/admin-coding/GoldenDatasetMenuItems";
import {
  OrphanedAsteriskCheckFailedNotice,
  RestrictionCheckFailedNotice,
} from "@/ai-coding-outputs/PrimaryDiagnosisWarning";
import { GOLDEN_SOURCE_BACKGROUND } from "@/ai-coding-outputs/admin-coding/goldenSourceBackground";
import { useGoldenDataset } from "@/ai-coding-outputs/admin-coding/useGoldenDataset";
import { InlineFlags, procedureInlineFlags, useInlineDiagnosisFlags } from "@/ai-coding-outputs/inlineCodeFlags";
import { extractRejectionNotes, extractRejectionReason } from "@/ai-coding-outputs/admin-coding/rejectionReason";
import type { GoldenDatasetSource } from "@/api/reviews";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ConfirmationDialog } from "@/components/ui/ConfirmationDialog";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ScrollArea } from "@/components/ui/scroll-area";
import type { UseMutationResult } from "@tanstack/react-query";
import {
  ArrowDown,
  ArrowUp,
  Ban,
  ExternalLink,
  FileText,
  Search,
  History,
  MoreVertical,
  RotateCcw,
} from "lucide-react";
import { memo, useEffect, useMemo, useState } from "react";
import type { DiagnosisEvidenceRow } from "../human-coding/types";
import type { UnmatchedLeadTerm } from "../human-coding/useUnmatchedLeadTerms";
import { UnmatchedLeadTermItem } from "../human-coding/UnmatchedLeadTermItem";
import { useDroppedCodesToggle } from "../human-coding/useDroppedCodesToggle";

export interface CodeReviewColumnProps {
  encounterId: string;
  isWrittenBack: boolean;
  /** A later delivery from another review or result replaced this one's, so its codes can be sent again. */
  isSuperseded: boolean;
  /** Status of the encounter's current write-back, named when this one's was superseded. */
  currentWriteBackStatus: string | null;
  /** A coder's review holds the encounter's current write-back, so sending these codes would replace approved ones. */
  isCurrentWriteBackFromReview?: boolean;
  reviewerName: string;
  reviewerColor: string;
  procedures: CodeItem[];
  diagnoses: CodeItem[];
  primaryDiagnosisIdentified?: boolean | null;
  notes?: string | null;
  hrgCode?: string | null;
  customerLabel: string;
  writeBackResultMutation: UseMutationResult<unknown, Error, void>;
  aiCodesForComparison?: {
    procedures: CodeItem[];
    diagnoses: CodeItem[];
  };
  outcome?: "approved" | "rejected" | null;
  linkOutToReviewMutation: UseMutationResult<unknown, Error, void>;
  /** Called when admin clicks void (opens confirmation in parent). */
  onVoidReview?: () => void;
  /** Called when admin clicks unvoid (opens confirmation in parent). */
  onUnvoidReview?: () => void;
  /** Whether to show void/unvoid buttons (admin or clinical_coder_admin). */
  showVoidUnvoidButton?: boolean;
  /** Whether this review is voided (cannot be sent to customer). */
  isVoided?: boolean;
  /** Dropped diagnosis codes — codes in evidence but not in active diagnoses */
  droppedCodes?: DiagnosisEvidenceRow[];
  /** Unmatched lead terms that could not be mapped to ICD-10 codes */
  unmatchedLeadTerms?: UnmatchedLeadTerm[];
  /** Set of code strings that were removed by a human reviewer — renders a red ring in the AI column */
  codesRemovedByHuman?: Set<string>;
  /** Label for the OPCS coding system version, e.g. "OPCS-4.11". Falls back to "OPCS-4". */
  opcsLabel?: string;
  /** Selects which paired-code rows apply; the Z08/Z09 rows are endoscopy only. */
  specialty?: EncounterSpecialty | null;
  patientAge?: number | null;
  patientSex?: string | null;
  /** ICD-10 codes the AI took from a previous episode; they show the trust badge wherever they appear. */
  historicCodes?: ReadonlySet<string>;
  /** Evidence callbacks — when provided, renders timeline & highlight buttons on diagnoses */
  evidenceCallbacks?: {
    onOpenTimeline: (code: string) => void;
    onToggleHighlight: (code: string) => void;
    hasTimeline: (code: string) => boolean;
    hasQuotes: (code: string) => boolean;
    focusedCode: string | null;
    activeTimelineCode: string | null;
  };
  goldenDatasetSource?: GoldenDatasetSource;
  isGoldenDatasetSupported?: boolean;
  onGoldenDatasetCodesChanged?: () => void;
}

const supersededTitle = (currentWriteBackStatus: string | null): string =>
  currentWriteBackStatus === null
    ? "Superseded by a later write-back"
    : `Superseded by a later write-back (${currentWriteBackStatus})`;

const CodeReviewColumn = memo(
  ({
    encounterId,
    reviewerName,
    reviewerColor,
    procedures,
    diagnoses,
    primaryDiagnosisIdentified,
    notes,
    hrgCode,
    customerLabel,
    aiCodesForComparison,
    outcome,
    isWrittenBack,
    isSuperseded,
    currentWriteBackStatus,
    isCurrentWriteBackFromReview = false,
    linkOutToReviewMutation,
    writeBackResultMutation,
    onVoidReview,
    onUnvoidReview,
    showVoidUnvoidButton,
    isVoided = false,
    droppedCodes,
    unmatchedLeadTerms,
    codesRemovedByHuman,
    opcsLabel,
    specialty,
    patientAge,
    patientSex,
    historicCodes,
    evidenceCallbacks,
    goldenDatasetSource,
    isGoldenDatasetSupported = true,
    onGoldenDatasetCodesChanged,
  }: CodeReviewColumnProps) => {
    const [isNotesDialogOpen, setIsNotesDialogOpen] = useState(false);
    const golden = useGoldenDataset({
      encounterId,
      source: goldenDatasetSource,
      supported: isGoldenDatasetSupported,
      diagnoses,
      procedures,
      onCodesChanged: onGoldenDatasetCodesChanged,
    });
    const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false);
    const { mutate: writeBackResult, isPending } = writeBackResultMutation;
    const { mutate: linkOutToReview, isPending: isLinkingOutToReview } = linkOutToReviewMutation;

    const isRejected = outcome === "rejected";

    // Extract rejection reason if rejected
    const rejectionReason = useMemo(() => {
      if (!isRejected) return null;
      return extractRejectionReason(notes);
    }, [isRejected, notes]);

    // Extract reviewer notes if rejected
    const reviewerNotes = useMemo(() => {
      if (!isRejected) return null;
      return extractRejectionNotes(notes);
    }, [isRejected, notes]);

    // Use red colors for rejected reviews, green for written back, muted for voided, otherwise reviewer color
    const borderColor = isRejected
      ? "#ef4444" // red-500
      : isVoided
        ? "#9ca3af" // gray-400
        : reviewerColor || "#6366f1";
    const statusBorderColor = isWrittenBack ? "#22c55e" : borderColor; // green-500
    const bgColor = isRejected
      ? "#fef2f2" // red-50
      : isVoided
        ? "#f3f4f6" // gray-100
        : `${borderColor}05`; // 5% opacity
    const codeListsBackground = isRejected || isVoided || !golden.isGoldenSource ? undefined : GOLDEN_SOURCE_BACKGROUND;
    const headerBgColor = isRejected
      ? "#fee2e2" // red-100
      : isVoided
        ? "#e5e7eb" // gray-200
        : `${borderColor}10`; // 10% opacity

    // Compute diff if AI codes are provided for comparison (and this is not the AI column)
    const diffResult = useMemo(() => {
      if (!aiCodesForComparison || isVoided) {
        return null;
      }
      return computeFullCodeDiff(
        procedures,
        diagnoses,
        aiCodesForComparison.procedures,
        aiCodesForComparison.diagnoses,
      );
    }, [aiCodesForComparison, procedures, diagnoses, isVoided]);

    // Use diff results if available, otherwise use original codes
    const displayProcedures: CodeItemWithDiff[] = useMemo(() => {
      // For human review columns, show codes with diff status
      if (diffResult) {
        return diffResult.procedures;
      }
      return procedures;
    }, [procedures, diffResult]);

    const displayDiagnoses: CodeItemWithDiff[] = useMemo(() => {
      // For human review columns, show codes with diff status
      if (diffResult) {
        return diffResult.diagnoses;
      }
      return diagnoses;
    }, [diagnoses, diffResult]);
    const incorrectPrimaryCode =
      primaryDiagnosisIdentified === false ? ((aiCodesForComparison?.diagnoses ?? diagnoses)[0]?.code ?? null) : null;
    const { flagsByIndex, daggerAsteriskCheckFailed, restrictionCheckFailed } = useInlineDiagnosisFlags({
      diagnoses: displayDiagnoses,
      specialty,
      patientAge,
      patientSex,
      historicCodes,
      incorrectPrimaryCode,
    });

    const { showDropped, toggleDropped } = useDroppedCodesToggle();

    // Helper function to get styling classes based on diff status
    const getDiffStyleClasses = (codeItem: CodeItemWithDiff, defaultBg: string, defaultBorder: string) => {
      // If no diff status, use default styling
      if (!codeItem.diffStatus) {
        return `${defaultBg} ${defaultBorder}`;
      }

      // Skip orange background for position-only changes (positionDirection present)
      // They will show arrow indicator instead
      if (codeItem.positionDirection) {
        return `${defaultBg} ${defaultBorder}`;
      }

      // Handle diff statuses
      switch (codeItem.diffStatus) {
        case "added":
          return "bg-green-50 border-green-300 border-l-[3px] border-green-400";
        case "changed":
          return "bg-amber-50 border-amber-300 border-l-[3px] border-amber-400";
        case "removed":
          return "bg-red-50 border-red-300";
        default:
          return `${defaultBg} ${defaultBorder}`;
      }
    };

    // Helper function to get diff status badge
    const getDiffBadge = (codeItem: CodeItemWithDiff) => {
      if (!codeItem.diffStatus || codeItem.diffStatus === "unchanged") {
        return null;
      }

      const badgeVariants = {
        added: { className: "bg-green-100 text-green-700", label: "Added" },
        removed: { className: "bg-red-100 text-red-700", label: "Removed" },
        changed: { className: "bg-amber-100 text-amber-700", label: "Changed" },
      };

      const variant = badgeVariants[codeItem.diffStatus];
      if (!variant) return null;

      return (
        <Badge variant="outline" className={`text-xs px-1.5 py-0.5 ${variant.className}`}>
          {variant.label}
        </Badge>
      );
    };

    // Helper function to render position arrow indicator
    const getPositionArrow = (codeItem: CodeItemWithDiff) => {
      if (!codeItem.positionDirection) {
        return null;
      }

      const ArrowIcon = codeItem.positionDirection === "up" ? ArrowUp : ArrowDown;
      return (
        <span title={`Position moved ${codeItem.positionDirection}`} className="inline-flex items-center">
          <ArrowIcon className="h-4 w-4 text-amber-600" />
        </span>
      );
    };

    const handleSubmit = () => {
      if (isWrittenBack) {
        setIsConfirmDialogOpen(false);
        return;
      }
      writeBackResult();
    };

    useEffect(() => {
      if (isWrittenBack && isConfirmDialogOpen) {
        setIsConfirmDialogOpen(false);
      }
    }, [isWrittenBack, isConfirmDialogOpen]);

    return (
      <div
        data-testid="review-column"
        className={`flex flex-col h-full min-w-[280px] border-l-4 border-r ${
          isRejected ? "border-red-300" : isWrittenBack ? "border-green-400" : "border-gray-200"
        }`}
        style={{
          borderLeftColor: statusBorderColor,
          backgroundColor: bgColor,
        }}
      >
        {/* Header */}
        <div
          data-testid="review-column-header"
          className={`p-3 border-b font-medium text-sm ${isRejected ? "border-red-200" : "border-gray-200"}`}
          style={{ backgroundColor: headerBgColor }}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className={`font-semibold ${isRejected ? "text-red-900" : ""} ${isVoided ? "text-gray-600" : ""}`}>
                {reviewerName}
              </span>
              {!isRejected && !isVoided && <HrgPill hrgCode={hrgCode} />}
              {isVoided && (
                <Badge variant="secondary" className="border border-red-200/60 bg-red-50/70 text-xs text-red-500/80">
                  Voided
                </Badge>
              )}
            </div>
            <div className="flex items-center gap-2">
              {isSuperseded && (
                <Badge variant="outline" className="text-xs" title={supersededTitle(currentWriteBackStatus)}>
                  Superseded
                </Badge>
              )}
              {isRejected && (
                <Badge variant="destructive" className="text-xs">
                  Rejected
                </Badge>
              )}
              {golden.isGoldenSource && (
                <span role="img" aria-label="In golden dataset" title="In golden dataset">
                  🌟
                </span>
              )}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-6 w-6 p-0"
                    title="Actions"
                    aria-label="Actions for this review"
                  >
                    <MoreVertical className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => linkOutToReview()} disabled={isLinkingOutToReview}>
                    <ExternalLink className="mr-2 h-4 w-4" />
                    Create review with these codes
                  </DropdownMenuItem>
                  <GoldenDatasetMenuItems golden={golden} />
                  {notes && notes.trim().length > 0 && (
                    <DropdownMenuItem onClick={() => setIsNotesDialogOpen(true)}>
                      <FileText className="mr-2 h-4 w-4" />
                      View notes
                    </DropdownMenuItem>
                  )}
                  {(onVoidReview || onUnvoidReview) && (
                    <>
                      <DropdownMenuSeparator />
                      {showVoidUnvoidButton && onVoidReview && (
                        <DropdownMenuItem onClick={onVoidReview} className="text-destructive focus:text-destructive">
                          <Ban className="mr-2 h-4 w-4" />
                          Void review
                        </DropdownMenuItem>
                      )}
                      {showVoidUnvoidButton && onUnvoidReview && (
                        <DropdownMenuItem onClick={onUnvoidReview}>
                          <RotateCcw className="mr-2 h-4 w-4" />
                          Restore review
                        </DropdownMenuItem>
                      )}
                    </>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>

          {notes != null && notes.trim().length > 0 && (
            <Dialog open={isNotesDialogOpen} onOpenChange={setIsNotesDialogOpen}>
              <DialogContent className="max-w-2xl">
                <DialogHeader>
                  <DialogTitle>{reviewerName}'s Review Notes</DialogTitle>
                </DialogHeader>
                <ScrollArea className="max-h-96">
                  <div className="text-sm text-gray-600 whitespace-pre-wrap">{notes}</div>
                </ScrollArea>
              </DialogContent>
            </Dialog>
          )}
        </div>

        {/* Scrollable Content */}
        <ScrollArea
          data-testid="review-column-codes"
          className="flex-1"
          style={{ backgroundColor: codeListsBackground }}
        >
          <div className="p-3 space-y-4">
            {/* Rejection Reason - Display prominently at top for rejected reviews */}
            {isRejected && rejectionReason && (
              <div className="bg-red-100 border border-red-300 rounded-md p-3 mb-4">
                <div className="text-sm font-semibold text-red-900 mb-1">Rejection Reason:</div>
                <div className="text-sm text-red-700">{rejectionReason}</div>
                {reviewerNotes && (
                  <>
                    <div className="text-sm font-semibold text-red-900 mt-3 mb-1">Reviewer Notes:</div>
                    <div className="text-sm text-red-700 whitespace-pre-wrap">{reviewerNotes}</div>
                  </>
                )}
              </div>
            )}

            {/* Procedures */}
            {displayProcedures.length > 0 && (
              <div>
                <h3 className="text-sm font-medium mb-2 text-gray-700">Procedures ({opcsLabel ?? "OPCS-4"})</h3>
                <div className="space-y-1">
                  {displayProcedures.map((procedure, index) => {
                    const isRemoved = procedure.diffStatus === "removed";
                    return (
                      <div
                        key={`${procedure.code}-${index}`}
                        className={`p-2 border rounded-md ${getDiffStyleClasses(
                          procedure,
                          "bg-yellow-50",
                          "border-yellow-200",
                        )} ${codesRemovedByHuman?.has(procedure.code) ? "ring-2 ring-red-400" : ""}`}
                      >
                        <div className="flex items-center justify-between mb-1">
                          <span
                            className={`font-mono text-sm font-medium ${isRemoved ? "line-through text-gray-500" : ""}`}
                          >
                            {procedure.code}
                          </span>
                          <div className="flex items-center gap-1">
                            <InlineFlags flags={procedureInlineFlags(procedure, index, specialty)} />
                            {getPositionArrow(procedure)}
                            {getDiffBadge(procedure)}
                          </div>
                        </div>
                        {procedure.description && (
                          <div
                            className={`text-xs line-clamp-2 ${
                              isRemoved ? "text-gray-400 line-through" : "text-gray-600"
                            }`}
                          >
                            {procedure.description}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Diagnoses (ICD-10) */}
            {displayDiagnoses.length > 0 && (
              <div>
                <h3 className="text-sm font-medium mb-2 text-gray-700">Diagnosis (ICD-10)</h3>
                {daggerAsteriskCheckFailed && <OrphanedAsteriskCheckFailedNotice />}
                {restrictionCheckFailed && <RestrictionCheckFailedNotice />}
                <div className="space-y-1">
                  {displayDiagnoses.map((diagnosis, index) => {
                    const isRemoved = diagnosis.diffStatus === "removed";
                    return (
                      <div
                        key={`${diagnosis.code}-${index}`}
                        className={`p-2 border rounded-md ${getDiffStyleClasses(
                          diagnosis,
                          "bg-purple-50",
                          "border-purple-200",
                        )} ${codesRemovedByHuman?.has(diagnosis.code) ? "ring-2 ring-red-400" : ""}`}
                      >
                        <div className="flex items-center justify-between mb-1">
                          <span
                            className={`font-mono text-sm font-medium ${isRemoved ? "line-through text-gray-500" : ""}`}
                          >
                            {diagnosis.code}
                          </span>
                          <div className="flex items-center gap-1">
                            <InlineFlags flags={flagsByIndex[index] ?? []} />
                            {getPositionArrow(diagnosis)}
                            {getDiffBadge(diagnosis)}
                            {index === 0 && !isRemoved && (
                              <Badge variant="default" className="text-xs px-1.5 py-0.5">
                                Primary
                              </Badge>
                            )}
                          </div>
                        </div>
                        {diagnosis.description && (
                          <div
                            className={`text-xs line-clamp-2 ${
                              isRemoved ? "text-gray-400 line-through" : "text-gray-600"
                            }`}
                          >
                            {diagnosis.description}
                          </div>
                        )}
                        {evidenceCallbacks && (
                          <div className="flex items-center gap-0.5 ml-auto shrink-0">
                            {evidenceCallbacks.hasTimeline(diagnosis.code) && (
                              <Button
                                size="sm"
                                variant="ghost"
                                className={`h-5 w-5 p-0 ${
                                  evidenceCallbacks.activeTimelineCode === diagnosis.code
                                    ? "text-sky-500"
                                    : "text-gray-400 hover:text-gray-600"
                                }`}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  evidenceCallbacks.onOpenTimeline(diagnosis.code);
                                }}
                                aria-label={`View timeline for ${diagnosis.code}`}
                              >
                                <History className="h-3 w-3" />
                              </Button>
                            )}
                            <Button
                              size="sm"
                              variant="ghost"
                              className={`h-5 w-5 p-0 ${
                                !evidenceCallbacks.hasQuotes(diagnosis.code)
                                  ? "text-gray-300 cursor-not-allowed"
                                  : evidenceCallbacks.focusedCode === diagnosis.code
                                    ? "text-sky-500"
                                    : "text-gray-400 hover:text-gray-600"
                              }`}
                              disabled={!evidenceCallbacks.hasQuotes(diagnosis.code)}
                              onClick={(e) => {
                                e.stopPropagation();
                                evidenceCallbacks.onToggleHighlight(diagnosis.code);
                              }}
                              aria-label={`Toggle evidence highlight for ${diagnosis.code}`}
                            >
                              <Search className="h-3 w-3" />
                            </Button>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Dropped codes section */}
            {droppedCodes && droppedCodes.length > 0 && (
              <div>
                <button
                  className="w-full flex items-center gap-2 text-xs text-gray-500 hover:text-gray-700 py-1"
                  onClick={toggleDropped}
                >
                  <div className="flex-1 h-px bg-gray-200" />
                  <span className="flex items-center gap-1 px-2 py-0.5 rounded bg-gray-100 border border-gray-200">
                    {showDropped ? "Hide" : "Show"} dropped ({droppedCodes.length})
                  </span>
                  <div className="flex-1 h-px bg-gray-200" />
                </button>
                {showDropped && (
                  <div className="mt-2 space-y-1 opacity-50">
                    {droppedCodes.map((dropped) => (
                      <div
                        key={dropped.icd10Code}
                        className="p-1.5 border border-dashed border-gray-300 rounded-md flex items-center gap-2"
                      >
                        <div className="flex-1 min-w-0">
                          <span className="font-mono text-sm text-gray-400 line-through">{dropped.icd10Code}</span>
                          <div className="text-xs text-gray-400 truncate">{dropped.description}</div>
                        </div>
                        {evidenceCallbacks?.hasTimeline(dropped.icd10Code) && (
                          <Button
                            size="sm"
                            variant="ghost"
                            className={`h-5 w-5 p-0 ${
                              evidenceCallbacks.activeTimelineCode === dropped.icd10Code
                                ? "text-purple-500"
                                : "text-gray-400 hover:text-gray-600"
                            }`}
                            onClick={() => evidenceCallbacks.onOpenTimeline(dropped.icd10Code)}
                            aria-label={`View timeline for dropped ${dropped.icd10Code}`}
                          >
                            <History className="h-3 w-3" />
                          </Button>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Unmatched lead terms section */}
            {unmatchedLeadTerms && unmatchedLeadTerms.length > 0 && (
              <div className="space-y-1.5">
                <p className="text-xs font-medium text-muted-foreground">Unmatched terms</p>
                {unmatchedLeadTerms.map((term, i) => (
                  <UnmatchedLeadTermItem
                    key={`${term.leadTerm}-${i}`}
                    term={term}
                    onToggleHighlight={evidenceCallbacks?.onToggleHighlight}
                    isFocused={evidenceCallbacks?.focusedCode === `unmatched::${term.leadTerm}`}
                  />
                ))}
              </div>
            )}

            {/* No codes message */}
            {displayProcedures.length === 0 && displayDiagnoses.length === 0 && (
              <div className={`text-center py-8 ${isRejected ? "text-red-600" : "text-gray-500"}`}>
                <div className="mb-2">No codes assigned</div>
                <div className="text-sm">
                  {isRejected
                    ? "Review was rejected - codes not output"
                    : !aiCodesForComparison
                      ? "AI did not assign codes"
                      : "Reviewer did not assign codes"}
                </div>
              </div>
            )}
          </div>
        </ScrollArea>

        {/* Submit Button - Hidden for rejected or voided reviews; voided shows explanation */}
        {customerLabel && !isRejected && (
          <div className="p-3 border-t border-gray-200">
            {isVoided ? (
              <div className="rounded-md border border-red-200/60 bg-red-50/70 px-3 py-2 text-sm text-red-500/80">
                Voided – cannot send to customer
              </div>
            ) : (
              <div className="space-y-2">
                {isCurrentWriteBackFromReview && (
                  <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                    Current write-back is from a coder's review
                  </div>
                )}
                <Button
                  onClick={() => setIsConfirmDialogOpen(true)}
                  disabled={isPending || isWrittenBack}
                  className="w-full"
                  variant="default"
                >
                  {isPending ? "Submitting..." : isWrittenBack ? "Already written back" : `Send to ${customerLabel}`}
                </Button>
              </div>
            )}
          </div>
        )}

        {/* Confirmation Dialog - not shown for voided reviews */}
        <ConfirmationDialog
          isOpen={isConfirmDialogOpen && !isWrittenBack && !isVoided}
          onClose={() => setIsConfirmDialogOpen(false)}
          onConfirm={handleSubmit}
          title="Confirm Submission"
          description={
            isCurrentWriteBackFromReview
              ? `Are you sure you want to submit ${reviewerName}'s result to ${customerLabel}? The current write-back is from a coder's review; sending replaces it with these codes.`
              : `Are you sure you want to submit ${reviewerName}'s result to ${customerLabel}?`
          }
          confirmText="Submit"
          variant="success"
          isLoading={isPending}
        />
      </div>
    );
  },
);

CodeReviewColumn.displayName = "CodeReviewColumn";

export default CodeReviewColumn;
